import {
  checkoutV202604,
  isApiError,
  type FetchClient,
  type operations,
} from "@fluid-app/checkout-api-client";
import { fetchApi } from "@/lib/api-helpers";
import { z } from "zod";
import {
  cartResponseSchema,
  type CartResponse,
} from "@/networking/carts.types";
import { createPublicCheckoutApiClient } from "@/lib/checkout-api-client";

// Re-export every schema/type that previously lived in this file so existing
// consumers (importing from `@/networking/carts.api`) continue to work after
// the schemas were extracted into `carts.types.ts`.
export {
  cartCountrySchema,
  variantOptionSchema,
  baseProductSchema,
  bundledItemSchema,
  cartItemSchema,
  enrollmentItemSchema,
  cartResponseSchema,
  type SubscriptionPlan,
  type VariantOption,
  type BaseProduct,
  type Variant,
  type BundledItem,
  type CartItem,
  type EnrollmentItem,
  type EnrollmentPack,
  type AvailablePaymentMethod,
  type CartResponse,
} from "@/networking/carts.types";

type GetCartResponse =
  operations["checkout_v2026_04_get_cart"]["responses"][200]["content"]["application/json"];

type GetCartQuery =
  operations["checkout_v2026_04_get_cart"]["parameters"]["query"];

function createHighPriorityPublicCheckoutApiClient(): FetchClient {
  const client = createPublicCheckoutApiClient();

  return {
    ...client,
    get: <TResponse = unknown>(
      endpoint: string,
      params?: Record<string, unknown>,
      options?: Parameters<typeof client.get>[2],
    ): Promise<TResponse> =>
      client.get<TResponse>(endpoint, params, {
        ...options,
        priority: "high",
      }),
  };
}

const highPriorityPublicCheckoutApiClient =
  createHighPriorityPublicCheckoutApiClient();

export async function fetchCart(
  cartToken: string,
  paymentMethodsIncludes?: string | null,
): Promise<CartResponse | null> {
  try {
    const params = paymentMethodsIncludes
      ? ({
          payment_methods_includes: paymentMethodsIncludes,
        } satisfies GetCartQuery)
      : undefined;

    const response: GetCartResponse =
      await checkoutV202604.checkout_v2026_04_get_cart(
        highPriorityPublicCheckoutApiClient,
        cartToken,
        params,
      );

    // The OpenAPI cart schema intentionally documents only the stable top-level
    // fields while the runtime response remains the full CartBlueprinter shape.
    return response as CartResponse;
  } catch (error) {
    if (isApiError(error) && error.status === 404) {
      return null;
    }
    throw error;
  }
}

export async function recalculateCart(
  cartToken: string,
  paymentMethodsIncludes?: string | null,
) {
  let endpoint = `/carts/${cartToken}/recalculate`;
  if (paymentMethodsIncludes) {
    endpoint += `?payment_methods_includes=${paymentMethodsIncludes}`;
  }
  return fetchApi({
    endpoint,
    method: "POST",
    outputSchema: cartResponseSchema,
  });
}

export async function updateCartAttributes({
  cartToken,
  email,
  phone,
}: {
  cartToken: string;
  email?: string;
  phone?: string;
}) {
  const payload: Record<string, string> = {};

  if (email !== undefined) {
    payload.email = email;
  }

  if (phone !== undefined) {
    payload.phone = phone;
  }

  return fetchApi({
    endpoint: `/carts/${cartToken}`,
    method: "PATCH",
    input: payload,
    outputSchema: cartResponseSchema,
  });
}

export async function updateCartPhone({
  cartToken,
  phone,
}: {
  cartToken: string;
  phone: string;
}) {
  return fetchApi({
    endpoint: `/carts/${cartToken}`,
    method: "PATCH",
    input: { phone },
    outputSchema: cartResponseSchema,
  });
}

const cartItemInputSchema = z.object({
  variant_id: z.number(),
  quantity: z.number(),
});

export async function updateCartItems(
  cartToken: string,
  cartItems: Array<{
    variant_id: number;
    quantity: number;
  }>,
) {
  return fetchApi({
    endpoint: `/carts/${cartToken}/items`,
    method: "POST",
    input: { items: cartItems },
    inputSchema: z.object({
      items: z.array(cartItemInputSchema),
    }),
    outputSchema: cartResponseSchema,
  });
}

export async function deleteCartItem(cartToken: string, itemId: number) {
  return fetchApi({
    endpoint: `/carts/${cartToken}/items/${itemId}`,
    method: "DELETE",
    outputSchema: cartResponseSchema,
  });
}

export async function removeEnrollmentCartItem(
  cartToken: string,
  itemId: string,
  options?: {
    fluidShop?: string;
    fluidSession?: string;
  },
) {
  let endpoint = `/public/v2025-06/commerce/carts/${cartToken}/items/${itemId}`;

  const queryParams = new URLSearchParams();
  if (options?.fluidShop) {
    queryParams.append("fluid_shop", options.fluidShop);
  }
  if (options?.fluidSession) {
    queryParams.append("fluid_session", options.fluidSession);
  }

  if (queryParams.toString()) {
    endpoint += `?${queryParams.toString()}`;
  }

  return fetchApi({
    endpoint,
    method: "DELETE",
    outputSchema: cartResponseSchema,
  });
}

export async function updateCartLanguage(
  cartToken: string,
  language: { language_iso: string },
) {
  return fetchApi({
    endpoint: `/carts/${cartToken}/change_language`,
    method: "PATCH",
    input: language,
    outputSchema: cartResponseSchema,
  });
}

export async function updateCartVolumeRep(
  cartToken: string,
  volumeRepId: number,
  jwt: string,
) {
  return fetchApi({
    endpoint: `/carts/${cartToken}/volume_rep`,
    method: "PATCH",
    input: {
      volume_rep_id: volumeRepId,
      order_on_behalf_of: true,
    },
    outputSchema: cartResponseSchema,
    fetchOptions: {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      },
    },
  });
}

export async function updateCartItemVariant(
  cartToken: string,
  itemId: number,
  variantId: number,
  quantity: number,
) {
  return fetchApi({
    endpoint: `/carts/${cartToken}/items/${itemId}/variant`,
    method: "PATCH",
    input: { variant_id: variantId, quantity: quantity },
    outputSchema: cartResponseSchema,
  });
}

export async function applyPoints(cartToken: string, pointsAmount: number) {
  return fetchApi({
    endpoint: `/carts/${cartToken}/apply_points`,
    method: "POST",
    inputSchema: z.object({
      points_amount: z.number().int().positive(),
    }),
    input: {
      points_amount: pointsAmount,
    },
    outputSchema: cartResponseSchema,
  });
}

export async function removePoints(cartToken: string) {
  return fetchApi({
    endpoint: `/carts/${cartToken}/remove_points`,
    method: "DELETE",
    outputSchema: cartResponseSchema,
  });
}

export async function syncCustomer({
  cartToken,
  jwt,
}: {
  cartToken: string;
  jwt: string;
}) {
  return fetchApi({
    endpoint: `/carts/${cartToken}/sync_customer`,
    method: "POST",
    outputSchema: cartResponseSchema,
    fetchOptions: {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      },
    },
  });
}

export async function updateCartMetaData({
  cartToken,
  metaData,
}: {
  cartToken: string;
  metaData: Record<string, unknown>;
}) {
  return fetchApi({
    endpoint: `/carts/${cartToken}/metadata`,
    method: "PATCH",
    input: { metadata: metaData },
    outputSchema: cartResponseSchema,
  });
}
